import cn from 'classnames'
import styles from 'components/atoms/Flex/Flex.styl'
import themes from 'styles/base/themes.styl'
import { toCamelCase } from 'helpers/formatHelpers'
import { FlexOptions } from 'components/atoms/Flex/Flex'
import { Theme, ThemeStyle } from 'interfaces/component'

const defaultValues: FlexOptions = {
  horizontal: false,
  gap: 'defaultSize',
  padding: 'none',
  verticallyCenter: false,
  horizontallyCenter: false,
  alignRight: false,
  shrinkHeight: false, // set children to fit-content
  shrinkWidth: false, // set children to fit-content
  fullWidth: false, // full width of the whole container
}

// FLEX
// -----------------------------------------------------------------------------------------
/**
 * Returns a className with:
 ** width: 100%
 *
 * Use this in any Component and pass to the 'ClassName'.
 * Has to be enabled with a truthy parameter
 */
export const useFullWidth = (shouldUseFullWidth = false): string => {
  return !!shouldUseFullWidth ? styles.fullWidth : ''
}

/**
 * Returns classes for a flexbox layout. Params control:

 * Use this in any Component and pass to the 'ClassName'
 *
 * @param {FlexOptions} flexOptions - Preset parameters that control the layout
 *
 * @param {boolean} horizontal - Control the orientation of the layout
 * @param {boolean} verticallyCenter
 * @param {boolean} horizontallyCenter -
 * @param {boolean} alignRight -
 * @param {boolean} shrinkWidth - Hug content horizontally
 * @param {boolean} shrinkHeight - Hug content vertically
 * @param {boolean} responsive - if horizontal, flips the orientation on small screens
 * @param {boolean} rounded - rounds the corners based on the padding amount
 * @param {boolean} marginBottom - sets a bottom margin based on the padding
 * @param {FlexPadding} padding -
 * @param {FlexGap} gap - Space between elements
 */
export const useFlexClassNames = (flexOptions: FlexOptions = defaultValues) => {
  const {
    padding,
    elevation,
    gap = 'defaultSize',
    marginBottom = 'none',
    verticallyCenter = false,
    horizontallyCenter = false,
    alignRight = false,
    shrinkHeight = false,
    shrinkWidth = false,
    fullWidth = false,
    horizontal = false,
    responsive = false,
    rounded = false,
  } = flexOptions

  const classNames = cn(
    elevation ? styles[elevation + 'Elevation' as keyof typeof styles] : undefined,
    padding ? styles[toCamelCase(padding) + 'Padding' as keyof typeof styles] : undefined,
    marginBottom ? styles[toCamelCase(marginBottom) + 'MarginBottom' as keyof typeof styles] : undefined,
    styles.flex,
    styles[gap], {
      [styles.vertical]: !horizontal,
      [styles.verticallyCenter]: verticallyCenter,
      [styles.horizontallyCenter]: horizontallyCenter,
      [styles.alignRight]: alignRight,
      [styles.marginBottom]: !!marginBottom,
      [styles.padded]: !!padding,
      [styles.responsive]: responsive,
      [styles.shrinkHeight]: shrinkHeight,
      [styles.shrinkWidth]: shrinkWidth,
      [styles.fullWidth]: fullWidth,
      [styles.rounded]: rounded,
    }
  )

  return classNames
}


// THEMES
// -----------------------------------------------------------------------------------------
/**
 * Returns theme classes based on Status' design tokens.
 * Allows you to control the colors of your component
 * without creating a new .css/.styl file.
 *
 * Use this in any Component and pass to the 'ClassName'
 *
 * @param {Theme} theme - Sets the color scheme of a component
 * @param {ThemeStyle} style - Determines the way the color scheme is applies
 */
export const useThemeClassNames = (theme?: Theme, style: ThemeStyle = 'solid') => {
  const classNames = cn(
    !!theme ? themes[theme as keyof typeof themes] : undefined,
    !!style ? themes[style as keyof typeof themes] : undefined,
  )
  return classNames
}


// COMBINED FLEX & THEMES
// -----------------------------------------------------------------------------------------
/**
 * Allows you to control the layout AND colors of your component
 * without creating a new .css/.styl file.
 *
 * Use this in any Component and pass to the 'ClassName'
 */
export const useThemedFlexClassNames = (theme: Theme, style: ThemeStyle = 'solid', flexOptions: FlexOptions) => {
  const themeClasses = useThemeClassNames(theme, style)
  const flexClasses = useFlexClassNames(flexOptions)
  const classNames = cn(themeClasses, flexClasses)
  return classNames
}
